import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import managerRowStyles from './ManagerRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import ManagerRow from './ManagerRow';
import ListContent from '../composites/ListContent';
import AddManagerDialog from './AddManagerDialog';
import { useSessionAction, useSessionData, useUserAction } from '../../providers';
import { fetchManagersPageByGymId } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { usePaginatedListQuery } from '../../hooks/usePaginatedListQuery';

import type { Manager, WithOptionalId } from '../../types';

const managerListTitles = ['Manager Name', 'Options'];

const Managers: React.FC = () => {
  const { managers: allGymManagers, gym, loginManager } = useSessionData();
  const { ensureGymManagersLoaded } = useSessionAction();
  const { createManager, updateManager } = useUserAction();
  const queryClient = useQueryClient();
  const [selectedManager, setSelectedManager] = useState<string>('');
  const [isAddManagerModalOpened, setIsAddManagerModalOpened] = useState(false);

  const managersListQuery = usePaginatedListQuery<Manager>({
    enabled: Boolean(gym?.id),
    queryKey: (params) => queryKeys.managers.gymPage(gym?.id || '', params),
    queryFn: (params) => fetchManagersPageByGymId(gym!.id, params),
  });

  useEffect(() => {
    if (!gym?.id) {
      return;
    }
    void ensureGymManagersLoaded(gym.id);
  }, [ensureGymManagersLoaded, gym?.id]);

  const filteredManagers = useMemo(() => {
    return managersListQuery.items
      .map((manager) => {
        const hydratedManager = allGymManagers[manager.id];
        return {
          ...hydratedManager,
          ...manager,
          gymIds:
            manager.gymIds ?? hydratedManager?.gymIds ?? (gym ? [gym.id] : []),
        };
      })
      .sort(
        (a, b) =>
          a.firstName
            ?.toLowerCase()
            .localeCompare(b.firstName?.toLowerCase() || '') || 0,
      );
  }, [allGymManagers, gym, managersListQuery.items]);

  const managersPagination = {
    page: managersListQuery.page,
    pageSize: managersListQuery.pageSize,
    totalElements: managersListQuery.meta.totalElements,
    totalPages: managersListQuery.meta.totalPages,
    onPageChange: managersListQuery.setPage,
    onPageSizeChange: managersListQuery.setPageSize,
    disabled: managersListQuery.isFetching,
  };

  const invalidateManagersPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['managers'] });
  };

  if (!loginManager || !gym) {
    return (
      <EmptyState icon={<Person4OutlinedIcon />} text="No managers found." />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<Person4OutlinedIcon />}
      cta={{
        icon: <Person4OutlinedIcon color="primary" />,
        label: 'Add A Manager',
        onClick: () => setIsAddManagerModalOpened(true),
      }}
      text="No Managers Have Been Added"
    />
  );

  const addManager = async (
    manager: WithOptionalId<Manager>,
  ): Promise<{ success: boolean; emailAlreadyInUse?: boolean }> => {
    const result = await createManager(manager);
    if (result.success) {
      setIsAddManagerModalOpened(false);
      invalidateManagersPage();
    }
    return result;
  };

  const renderAddProgramDialog = () => (
    <AddManagerDialog
      onSubmit={addManager}
      onClose={() => setIsAddManagerModalOpened(false)}
      isOpen={isAddManagerModalOpened}
    />
  );

  const deleteManager = async (manager: Manager) => {
    await updateManager({ ...manager, isActive: false });
    invalidateManagersPage();
  };

  const renderContent = () => {
    return (
      <ListContent
        items={filteredManagers}
        isLoading={managersListQuery.isLoading}
        listHeaderClassName={managerRowStyles.listGrid}
        pagination={managersPagination}
        EmptyStateComponent={renderEmptyState}
        titles={managerListTitles}
        ItemComponent={({ data }) => (
          <ManagerRow
            manager={data}
            deleteManager={deleteManager}
            selected={selectedManager === data.id}
            onSelect={() => setSelectedManager(data.id)}
            key={data.id}
          />
        )}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredManagers.length) return '';
    return 'Search for Managers';
  };

  return (
    <ContentDrill
      title="Managers"
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Manager',
          onClick: () => setIsAddManagerModalOpened(true),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      searchQuery={managersListQuery.searchQuery}
      setSearchQuery={managersListQuery.setSearchQuery}
      disableSearch={false}
    >
      {renderContent()}
      {renderAddProgramDialog()}
    </ContentDrill>
  );
};

export default Managers;
